import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../api/queryKeys';
import { getToken } from '../lib/token';
import { refreshAccessToken } from '../lib/api';
import { getActiveOrgId } from '../lib/activeOrg';

export type SocketStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected';

const SOCKET_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Subscribes to the `/issues` Socket.IO namespace for a single issue and
 * invalidates the comments query on new activity. Also tracks whether the PM
 * is currently planning a response (`isPmThinking`).
 */
export function useIssueSocket(issueId: string | undefined): {
  isPmThinking: boolean;
  status: SocketStatus;
} {
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const [isPmThinking, setIsPmThinking] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!issueId) return;

    const socket: Socket = io(`${SOCKET_BASE}/issues`, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token: getToken() },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 8000,
    });

    setStatus('connecting');

    const subscribe = () => {
      setStatus('connected');
      socket.emit('subscribe', { issueId, orgId: getActiveOrgId() });
    };

    socket.on('connect', subscribe);
    socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));
    socket.on('disconnect', (reason: string) => {
      setStatus('disconnected');
      if (reason === 'io server disconnect') {
        setStatus('reconnecting');
        void refreshAccessToken().then((newToken) => {
          if (newToken) {
            socket.auth = { token: newToken };
            socket.connect();
          } else {
            setStatus('disconnected');
          }
        });
      }
    });
    socket.on('connect_error', () =>
      setStatus((s) => (s === 'connected' ? 'reconnecting' : 'disconnected')),
    );

    socket.on('comment-added', () => {
      void queryClient.invalidateQueries({ queryKey: qk.issueComments(issueId) });
    });

    socket.on('plan-run-started', () => {
      setIsPmThinking(true);
    });

    socket.on('plan-run-settled', () => {
      setIsPmThinking(false);
      void queryClient.invalidateQueries({ queryKey: qk.issueComments(issueId) });
    });

    return () => {
      socket.emit('unsubscribe', { issueId });
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [issueId, queryClient]);

  return { isPmThinking, status };
}
